import type {MissingRequiredField} from 'constants/SampleConstants';
import type {Tag} from 'folder-items/FileConstants';
import {REQUIRED_FIELD_ERROR_LABEL} from 'metadata/SchemaFieldValueError';
import type {SchemaValidationErrorRecord} from 'schemas/SchemaValidationConstants';
import * as TagUtil from 'schemas/tags/TagUtil';

// Only containers and plates carry the server-computed list, but callers often hold a union of storable types.
interface WithMissingRequiredFields {
  missingRequiredFields?: ReadonlyArray<MissingRequiredField> | null;
}

export function getMissingRequiredFields(
  storable: object | null | undefined
): ReadonlyArray<MissingRequiredField> {
  if (storable == null || !('missingRequiredFields' in storable)) {
    return [];
  }
  return (storable as WithMissingRequiredFields).missingRequiredFields ?? [];
}

export function hasMissingRequiredFields(storable: object | null | undefined): boolean {
  return getMissingRequiredFields(storable).length > 0;
}

export function formatMissingRequiredFieldNames(fields: ReadonlyArray<MissingRequiredField>): string {
  return fields.map((field) => field.displayName).join(', ');
}

export function getMissingRequiredFieldsMessage(fields: ReadonlyArray<MissingRequiredField>): string {
  const noun = fields.length === 1 ? 'field' : 'fields';
  return `Missing required ${noun}: ${formatMissingRequiredFieldNames(fields)}`;
}

/** Per-field warnings for a tag table, keyed by `tagSchemaField.id` like its errors. */
export function getMissingRequiredFieldWarningsByFieldId(
  tags: ReadonlyArray<Tag | null>,
  fields: ReadonlyArray<MissingRequiredField>
): {[fieldId: number]: Array<SchemaValidationErrorRecord>} {
  const missingFieldIds = new Set(fields.map((field) => field.id));
  const warningsByFieldId: {[fieldId: number]: Array<SchemaValidationErrorRecord>} = {};
  for (const tag of TagUtil.filterToSchemaTags(tags.filter((tag): tag is Tag => tag != null))) {
    if (missingFieldIds.has(tag.tagSchemaField.id)) {
      warningsByFieldId[tag.tagSchemaField.id] = [
        {
          errorLabel: REQUIRED_FIELD_ERROR_LABEL,
          message: `"${tag.tagSchemaField.name}" is a required field. Add a value.`,
          field: tag.tagSchemaField,
        },
      ];
    }
  }
  return warningsByFieldId;
}
